import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { getErrorMessage } from '@/lib/api-errors'
import { toast } from '@/lib/toast'

import { createTask, getTask, getTaskIssues, getTasks } from '../api/taskDetailApi'

import type { TaskFilterParams } from '../api/taskDetailApi'

export function useTasks(params?: TaskFilterParams) {
  return useQuery({
    queryKey: ['tasks', params],
    queryFn: () => getTasks(params),
  })
}

export const taskDetailQueryOptions = (taskId: string | undefined) =>
  queryOptions({
    queryKey: ['task', taskId] as const,
    queryFn: () => getTask(taskId as string),
    enabled: Boolean(taskId),
    staleTime: 1000 * 60 * 2,
  })

export const taskIssuesQueryOptions = (taskId: string | undefined) =>
  queryOptions({
    queryKey: ['task-issues', taskId] as const,
    queryFn: () => getTaskIssues(taskId as string),
    enabled: Boolean(taskId),
    staleTime: 1000 * 30,
  })

export function useTask(taskId: string | undefined) {
  return useQuery(taskDetailQueryOptions(taskId))
}

export function useTaskIssues(taskId: string | undefined) {
  return useQuery(taskIssuesQueryOptions(taskId))
}

export function useCreateTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.invalidateQueries({ queryKey: ['site-detail'] })
      queryClient.invalidateQueries({ queryKey: ['entity-detail'] })
      toast.success('Task created successfully.')
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to create task.')),
  })
}
